import React, { useId, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Slider, ToggleButton, ToggleButtonGroup, Typography, useTheme } from '@mui/material';
import { CartesianGrid, ComposedChart, Line, ReferenceArea, ResponsiveContainer, XAxis, YAxis } from 'recharts';
import {
    getPerformanceChartChrome, getPerformanceTooltipStyle, performanceCompactLabelSx, performanceToggleButtonSx, performanceToggleGroupSx,
} from '../../styles/performanceMsiStyles';
import {
    HIGH_UNCOVERED_SHARE, closestMonth, drawdownScale, eventAt, eventBands, eventsIn, monthLabel, recentSpan, thinMonth, timelinePoints, windowPoints,
    timelineTicks,
} from './exploreEnvironment';
import { formatPercent } from './exploreFormat';

// Theme tokens (recolourable in Appearance): a picked span in the accent, and
// each kind of event in the colour its chips and column tags use.
const ACCENT = 'rgb(var(--c-accent, 21 101 192))';
const ACCENT_FILL = 'rgb(var(--c-accent, 21 101 192) / 0.18)';
export const BAND_FILLS = Object.freeze({ found: 'var(--app-warning, #9a5b08)', yours: 'var(--app-event, #5b34b0)' });
const BAND_COLOURS = Object.freeze({ standard: 'grey for standard', found: 'amber for found', yours: 'violet for yours' });

// "grey for standard, amber for found and violet for yours", naming only the kinds shown.
function bandKinds(events) {
    const kinds = Object.keys(BAND_COLOURS).filter((tag) => events.some((event) => event.tag === tag)).map((tag) => BAND_COLOURS[tag]);
    return kinds.length > 1 ? `${kinds.slice(0, -1).join(', ')} and ${kinds[kinds.length - 1]}` : kinds.join('');
}
// Portfolio lines, validated for adjacent-pair colour-blind separation and
// 3:1 contrast on the light and dark surfaces.
const PORTFOLIO_COLORS = Object.freeze({
    light: Object.freeze(['#2a78d6', '#e0602b', '#14956a', '#b98000', '#d4588a', '#008300']),
    dark: Object.freeze(['#3987e5', '#d95926', '#199e70', '#c98500', '#d55181', '#008300']),
});

// A portfolio's usual colour: Actual and Position Sizing have their own, and
// saved optimized portfolios share the other four by id.
function usualSlot(key) {
    if (key === 'actual') return 0;
    if (key === 'position_sizing') return 1;
    const id = Number(/^optimized:(\d+)$/.exec(key)?.[1]);
    return Number.isSafeInteger(id) ? 2 + (id % 4) : 2;
}

/**
 * The compared portfolios' colours, in order: each takes its usual colour
 * when it is free and otherwise the next free one, so no two rows share a
 * colour and a portfolio keeps its colour while its usual one is free.
 */
export function rowColors(keys, dark = false) {
    const palette = PORTFOLIO_COLORS[dark ? 'dark' : 'light'];
    const taken = new Set();
    return keys.map((key) => {
        let slot = usualSlot(key);
        while (taken.has(slot) && taken.size < palette.length) slot = (slot + 1) % palette.length;
        taken.add(slot);
        return palette[slot];
    });
}

// Both y axes are this wide and both charts share these margins, so their
// plots line up month for month; the overlays below use the same frame.
const AXIS_WIDTH = 52;
const MARGIN = Object.freeze({ top: 8, right: 12, bottom: 0, left: 0 });
// Room under the market plot for its "Far" tick when the replay panel has the months.
const MARKET_MARGIN = Object.freeze({ ...MARGIN, bottom: 6 });
const PLOT_WIDTH = `(100% - ${AXIS_WIDTH + MARGIN.right}px)`;
// Text lines up with the plots, except on phones where the room matters more.
const PLOT_INDENT = { xs: 0, sm: `${AXIS_WIDTH}px` };
const X_AXIS_HEIGHT = 24;
const MARKET_HEIGHT = 132;
const REPLAY_HEIGHT = 208;
const MARKET_TICKS = Object.freeze([0, 50, 100]);
const MARKET_TICK_WORDS = Object.freeze({ 0: 'Far', 100: 'Close' });
const marketTick = (value) => MARKET_TICK_WORDS[value] || '';
const percentTick = (value) => formatPercent(value, 0);
const MIN_SPAN_MONTHS = 12;
// The years shown, back from the history's last month, as the rest of the
// Performance tab picks its window. The timeline is monthly and its last
// half year is too recent to compare, so the shortest is a year; one no
// shorter than the history is left out, since All shows the same.
const LOOKBACKS = Object.freeze([
    Object.freeze({ key: '1Y', months: 12 }),
    Object.freeze({ key: '3Y', months: 36 }),
    Object.freeze({ key: '5Y', months: 60 }),
    Object.freeze({ key: '10Y', months: 120 }),
]);
const ALL = 'All';
const FIRST_LOOKBACK = '10Y';
// A press that moves less than this within its month is a click, not a drag.
const CLICK_SLOP = 4;
const DASH = '4 3';
// Markets are actual history beside the replays: one neutral ink, told apart
// by a fixed dash each rather than three more hues beside the portfolios'.
const MARKET_DASHES = Object.freeze({ SPY: '8 4', QQQ: '2 3', IWM: '8 3 2 3', EFA: '12 3 2 3 2 3' });
const marketDash = (market) => MARKET_DASHES[market] || MARKET_DASHES.SPY;
const STATUS_NOTES = Object.freeze({ loading: 'loading', unavailable: 'can’t be replayed' });
const MARKET_STATUS_NOTES = Object.freeze({ loading: 'loading', unavailable: 'unavailable' });

const ordered = (first, second) => (first <= second ? { from: first, to: second } : { from: second, to: first });
const across = (fraction) => `calc(${AXIS_WIDTH}px + ${PLOT_WIDTH} * ${fraction})`;

// A market's own dash needs a longer key to show its pattern.
function LineKey({ color, width = 2, dashed = false, dash = null }) {
    const length = dash ? 26 : 18;
    return (
        <svg aria-hidden="true" width={length} height="8" viewBox={`0 0 ${length} 8`} style={{ flex: 'none' }}>
            <line x1="1" y1="4" x2={length - 1} y2="4" stroke={color} strokeWidth={width} strokeLinecap={dash ? 'butt' : 'round'}
                strokeDasharray={dash || (dashed ? DASH : undefined)} />
        </svg>
    );
}

LineKey.propTypes = { color: PropTypes.string.isRequired, width: PropTypes.number, dashed: PropTypes.bool, dash: PropTypes.string };

function TimelineTooltip({ point, scored, rows, markets, colors, marketColor, events, baselineLabel, style, sx }) {
    let market = <><b>{Math.round(point.score)}</b> / 100 like today</>;
    if (point.excluded) market = 'too recent to compare';
    else if (point.score === null) market = 'no earlier history to compare';
    const kinds = { standard: 'Standard event', yours: 'Your event', found: `Found for ${baselineLabel}` };
    // The panels' own labels describe them to assistive technology.
    return (
        <Box aria-hidden="true" data-testid="timeline-tooltip" style={style} sx={sx}>
            <strong>{monthLabel(point.month)}</strong>
            {scored && <div>Market: {market}</div>}
            {rows.map((row, index) => {
                const value = point[`drawdown${index}`];
                if (!row.line || !Number.isFinite(value)) return null;
                const thin = thinMonth(point, index);
                return (
                    <Box key={row.key} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <LineKey color={colors[index]} width={row.baseline ? 3 : 2} dashed={thin} />
                        <b>{formatPercent(value, 1)}</b>
                        <span>
                            {row.label}{row.baseline ? ' · baseline' : ''}
                            {thin ? ` · ${Math.round(point[`uncovered${index}`] * 100)}% without factor history` : ''}
                        </span>
                    </Box>
                );
            })}
            {markets.map((market, index) => {
                const value = point[`market${index}`];
                if (!market.line || !Number.isFinite(value)) return null;
                return (
                    <Box key={market.key} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <LineKey color={marketColor} width={1.5} dash={marketDash(market.market)} />
                        <b>{formatPercent(value, 1)}</b>
                        <span>{market.label} · actual</span>
                    </Box>
                );
            })}
            {/* "Found for Actual: Worst month #1", not the portfolio twice. */}
            {eventsIn(events, point.month).map((event) => <div key={event.id}>{kinds[event.tag] || 'Event'}: {event.chipName || event.name}</div>)}
        </Box>
    );
}

TimelineTooltip.propTypes = {
    point: PropTypes.object.isRequired,
    scored: PropTypes.bool.isRequired,
    rows: PropTypes.array.isRequired,
    markets: PropTypes.array.isRequired,
    colors: PropTypes.arrayOf(PropTypes.string).isRequired,
    marketColor: PropTypes.string.isRequired,
    events: PropTypes.array.isRequired,
    baselineLabel: PropTypes.string.isRequired,
    style: PropTypes.object.isRequired,
    sx: PropTypes.object.isRequired,
};

function PanelTitle({ title, note = null }) {
    return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', columnGap: 1, pl: PLOT_INDENT }}>
            <Typography component="h4" sx={{ m: 0, fontSize: 12.5, fontWeight: 600, lineHeight: 1.35 }}>{title}</Typography>
            {note && <Typography variant="caption" color="text.secondary">{note}</Typography>}
        </Box>
    );
}

PanelTitle.propTypes = { title: PropTypes.string.isRequired, note: PropTypes.string };

/**
 * The factor history as two linked panels on one monthly axis, each with its
 * own y axis. Above, how much the market's factor environment looked like
 * today's (0 far to 100 close; the last months are too recent to compare,
 * and `runUp` outlines the closest past pattern, the run-up today's readings
 * most resemble, which the caption names). Below, each `rows` portfolio's
 * replay: its worst drawdown each month below its highest point in the years
 * shown, which start it at zero, today's holdings replayed through past
 * factor returns, dashed where much of the book had no factor history; the
 * baseline draws heavier. Each
 * `markets` line is that ETF's actual worst drawdown each month, measured the
 * same way from its prices with dividends reinvested: grey, with its own
 * dash, never a replay.
 *
 * Both panels shade the table's `events` (grey standard and found events,
 * accent for yours; `selectedEventId`'s band deeper), share one crosshair
 * and tooltip, and show the same years: a lookback back from the last month,
 * 10 years at first, or any span of a year or more picked on the slider.
 * When any of the closest past pattern falls outside them, a note under the
 * slider offers the shortest lookback that shows it all. A click or tap on a band
 * reports its event through `onSelectEvent(id)`. Dragging across months on
 * either panel, by mouse or touch, reports the span on release through
 * `onSelect({ from, to })`; the caller's `selection` keeps it shaded while
 * it is open, and `anchorRef` receives an element over it, for a pop-out to
 * point at.
 */
export default function HistoryStrip({
    months, rows = [], markets = [], events = [], selectedEventId = null, runUp = null, contextNote,
    baselineLabel = 'the baseline', selection = null, onSelect, onSelectEvent, anchorRef = null,
}) {
    const theme = useTheme();
    const dark = theme.palette.mode === 'dark';
    const chrome = useMemo(() => getPerformanceChartChrome(theme), [theme]);
    const [hover, setHover] = useState(null);
    // A lookback's key, All, or the { from, to } months picked on the slider.
    const [years, setYears] = useState(FIRST_LOOKBACK);
    const lookbackGroup = useRef(null);
    const noteId = useId();
    // The press in progress, read by every pointer event; `dragSpan` shades it once it moves.
    const drag = useRef(null);
    const [dragSpan, setDragSpan] = useState(null);
    const all = useMemo(() => timelinePoints(months, rows.map((row) => row.line), markets.map((market) => market.line)),
        [months, rows, markets]);
    const last = all.length - 1;
    const lookbacks = LOOKBACKS.filter((option) => option.months < all.length);
    const startOf = (option) => last - option.months + 1;
    const lookback = lookbacks.find((option) => option.key === years);
    const pickedFrom = all.findIndex((point) => point.month === years?.from);
    const pickedTo = all.findIndex((point) => point.month === years?.to);
    // A lookback the history is too short for, or a picked span it no longer has, shows all of it.
    let lo = 0;
    let hi = last;
    if (lookback) lo = startOf(lookback);
    else if (pickedFrom >= 0 && pickedTo >= 0) [lo, hi] = [pickedFrom, pickedTo];
    // The toggle pressed is whichever shows these months, if any.
    const pressed = lo === 0 && hi === last ? ALL : (lookbacks.find((option) => hi === last && lo === startOf(option))?.key ?? null);
    // Each line starts the years shown at zero, as the rest of the Performance tab's windows do.
    const points = useMemo(() => windowPoints(all, lo, hi, rows.length, markets.length), [all, lo, hi, rows.length, markets.length]);
    const position = useMemo(() => new Map(points.map((point, at) => [point.month, at])), [points]);
    const colors = useMemo(() => rowColors(rows.map((row) => row.key), dark), [rows, dark]);
    const scored = all.some((point) => point.score !== null);
    const replayed = rows.some((row) => row.line);

    // The charts only change with the data, the table and the years shown:
    // the crosshair, the tooltip and a drag are drawn over them.
    const pointer = useRef(null);
    const charts = useMemo(() => {
        const shown = points.map((point) => point.month);
        const bands = eventBands(events, shown);
        const [runUpBand] = runUp ? eventBands([{ id: 'run-up', start_date: `${runUp.from}-01`, end_date: `${runUp.to}-01` }], shown) : [];
        const recent = recentSpan(points);
        // The run-up's label reads away from the nearer edge; "Too recent" sits at the bottom.
        const runUpLabel = runUpBand && shown.indexOf(runUpBand.x1) < shown.length / 2 ? 'insideTopLeft' : 'insideTopRight';
        const scale = drawdownScale(points, rows.length, markets.length);
        const handlers = {
            onMouseDown: (state, event) => pointer.current.press(state, event),
            onMouseMove: (state, event) => pointer.current.move(state, event),
            onMouseUp: (state, event) => pointer.current.release(state, event),
            onTouchStart: (state, event) => pointer.current.press(state, event),
            onTouchMove: (state, event) => pointer.current.move(state, event),
            onTouchEnd: (state, event) => pointer.current.release(state, event),
        };
        // Every band already lies within the shown months. Recharts' default drops one whose end
        // a rounding error puts past the plot's edge, as the last month's can at some widths.
        const inside = { ifOverflow: 'visible' };
        // The selected cell's event fills deeper; only the run-up is outlined.
        const areas = bands.map((band) => (
            <ReferenceArea key={band.id} x1={band.x1} x2={band.x2} fill={BAND_FILLS[band.tag] || chrome.secondaryText} stroke="none"
                fillOpacity={(band.tag === 'standard' ? 0.16 : 0.14) * (band.id === selectedEventId ? 3 : 1)} {...inside} />
        ));
        const lineProps = { type: 'linear', dot: false, connectNulls: false, isAnimationActive: false };
        const ticks = timelineTicks(points);
        // Within two years the ticks are quarters: the year on January, the month otherwise.
        const tickLabel = (month) => (points.length > 24 || month.endsWith('-01') ? month.slice(0, 4) : monthLabel(month).slice(0, 3));
        // The baseline draws last, on top.
        const order = rows.map((row, at) => at).sort((left, right) => Number(rows[left].baseline) - Number(rows[right].baseline));
        return {
            market: (
                <ResponsiveContainer>
                    <ComposedChart data={points} margin={MARKET_MARGIN} {...handlers}>
                        <CartesianGrid stroke={chrome.grid} vertical={false} />
                        <XAxis dataKey="month" scale="band" hide={replayed} ticks={ticks} interval={0} tickFormatter={tickLabel}
                            tick={{ fontSize: 11, fill: chrome.mutedText }} stroke={chrome.axis} height={X_AXIS_HEIGHT} />
                        <YAxis domain={[0, 100]} ticks={MARKET_TICKS} interval={0} width={AXIS_WIDTH} stroke={chrome.axis}
                            tick={{ fontSize: 11, fill: chrome.mutedText }} tickFormatter={marketTick} />
                        {areas}
                        {recent && (
                            <ReferenceArea x1={recent.x1} x2={recent.x2} fill={chrome.mutedText} fillOpacity={0.24} stroke="none" {...inside}
                                label={{ value: 'Too recent', position: 'insideBottomRight', fill: chrome.mutedText, fontSize: 11 }} />
                        )}
                        {runUpBand && (
                            <ReferenceArea x1={runUpBand.x1} x2={runUpBand.x2} fill="none" stroke={chrome.text} strokeWidth={1.5} {...inside}
                                label={{ value: 'Closest past pattern', position: runUpLabel, fill: chrome.text, fontSize: 11 }} />
                        )}
                        <Line dataKey="score" stroke={chrome.text} strokeWidth={2} {...lineProps} />
                    </ComposedChart>
                </ResponsiveContainer>
            ),
            replay: (
                <ResponsiveContainer>
                    <ComposedChart data={points} margin={MARGIN} {...handlers}>
                        <CartesianGrid stroke={chrome.grid} vertical={false} />
                        <XAxis dataKey="month" scale="band" ticks={ticks} interval={0} tickFormatter={tickLabel} height={X_AXIS_HEIGHT}
                            tick={{ fontSize: 11, fill: chrome.mutedText }} stroke={chrome.axis} />
                        <YAxis domain={scale.domain} ticks={scale.ticks} width={AXIS_WIDTH} stroke={chrome.axis}
                            tick={{ fontSize: 11, fill: chrome.mutedText }} tickFormatter={percentTick} />
                        {areas}
                        {/* Under the replays, which stay the panel's subject. */}
                        {markets.map((market, at) => (market.line ? (
                            <Line key={`${market.key}:actual`} dataKey={`market${at}`} stroke={chrome.secondaryText} strokeWidth={1.5}
                                strokeDasharray={marketDash(market.market)} {...lineProps} />
                        ) : null))}
                        {order.flatMap((at) => (rows[at].line ? [
                            <Line key={`${rows[at].key}:solid`} dataKey={`solid${at}`} stroke={colors[at]} strokeWidth={rows[at].baseline ? 2.5 : 1.5} {...lineProps} />,
                            <Line key={`${rows[at].key}:dashed`} dataKey={`dashed${at}`} stroke={colors[at]} strokeWidth={rows[at].baseline ? 2.5 : 1.5}
                                strokeDasharray={DASH} {...lineProps} />,
                        ] : []))}
                    </ComposedChart>
                </ResponsiveContainer>
            ),
        };
    }, [points, events, selectedEventId, runUp, rows, markets, colors, chrome, replayed]);

    // The pointer's x on screen and the month under it, across the plot of
    // the chart the event belongs to. A touch keeps going to the chart it
    // started on, so a touch drag runs on over the other panel. (Recharts'
    // own label for a band axis changes halfway through each month.)
    const pointerAt = (event) => {
        const chart = event?.currentTarget?.getBoundingClientRect?.();
        const x = (event?.changedTouches?.[0] ?? event)?.clientX;
        if (!chart || !Number.isFinite(x)) return { x, month: null };
        const share = (x - chart.left - AXIS_WIDTH) / (chart.width - AXIS_WIDTH - MARGIN.right);
        return { x, month: share >= 0 && share < 1 ? points[Math.floor(share * points.length)].month : null };
    };
    const endDrag = () => {
        drag.current = null;
        setDragSpan(null);
    };
    pointer.current = {
        press: (_, event) => {
            const { x, month } = pointerAt(event);
            if (!month) return;
            drag.current = { from: month, to: month, x, moved: false };
            // A tap shows its month, as a hover does.
            if (event.type === 'touchstart') setHover(month);
        },
        move: (_, event) => {
            const { x, month } = pointerAt(event);
            setHover(month);
            const current = drag.current;
            if (!current) return;
            // The button came up outside the window, so its release never arrived.
            if (event?.buttons === 0) {
                endDrag();
                return;
            }
            if (!month) return;
            const moved = current.moved || month !== current.from || Math.abs(x - current.x) >= CLICK_SLOP;
            if (moved && (month !== current.to || !current.moved)) {
                drag.current = { ...current, to: month, moved: true };
                setDragSpan(ordered(current.from, month));
            }
        },
        release: (_, event) => {
            const current = drag.current;
            if (!current) return;
            endDrag();
            // A touch is followed by emulated mouse events, which would release it again.
            if (event?.type === 'touchend') event.preventDefault();
            if (current.moved) {
                onSelect(ordered(current.from, pointerAt(event).month || current.to));
                return;
            }
            const picked = eventAt(events, current.from, selectedEventId);
            if (picked) onSelectEvent(picked.id);
        },
    };
    const leave = () => {
        setHover(null);
        endDrag();
    };

    // A span's place in the shown months, as shares of the plot's width.
    const frame = (span) => {
        if (!span || !points.length) return null;
        const from = span.from < points[0].month ? points[0].month : span.from;
        const to = span.to > points[points.length - 1].month ? points[points.length - 1].month : span.to;
        if (!position.has(from) || !position.has(to)) return null;
        return { left: position.get(from) / points.length, width: (position.get(to) - position.get(from) + 1) / points.length };
    };
    const shaded = frame(dragSpan || selection);
    const hovered = hover && position.has(hover) ? points[position.get(hover)] : null;
    const crosshair = hovered ? (position.get(hover) + 0.5) / points.length : null;
    const overlays = (plotBottom, anchor) => (
        <>
            {shaded && (
                <Box ref={anchor ? anchorRef : undefined} data-testid={anchor ? 'strip-selection' : undefined} aria-hidden="true" sx={{
                    position: 'absolute', pointerEvents: 'none', top: MARGIN.top, bottom: plotBottom, left: across(shaded.left),
                    width: `calc(${PLOT_WIDTH} * ${shaded.width})`, bgcolor: ACCENT_FILL, borderInline: `1px solid ${ACCENT}`,
                }} />
            )}
            {crosshair !== null && (
                <Box aria-hidden="true" data-testid="crosshair" sx={{
                    position: 'absolute', pointerEvents: 'none', top: MARGIN.top, bottom: plotBottom, left: across(crosshair),
                    borderLeft: `1px solid ${chrome.mutedText}`,
                }} />
            )}
        </>
    );
    // Beside the crosshair, or across the panel's top on phones.
    const tooltip = hovered && (
        <TimelineTooltip point={hovered} scored={scored} rows={rows} markets={markets} colors={colors} marketColor={chrome.secondaryText}
            events={events} baselineLabel={baselineLabel}
            style={{ ...getPerformanceTooltipStyle(theme), fontSize: 12, lineHeight: 1.45 }}
            sx={{
                position: 'absolute', zIndex: 2, pointerEvents: 'none', top: MARGIN.top,
                left: { xs: 0, sm: across(crosshair) }, right: { xs: 0, sm: 'auto' }, width: { xs: 'auto', sm: 'max-content' }, maxWidth: { sm: 300 },
                transform: { xs: 'none', sm: crosshair > 0.5 ? 'translateX(calc(-100% - 10px))' : 'translateX(10px)' },
            }} />
    );

    const first = monthLabel(points[0]?.month);
    const lastShown = monthLabel(points[points.length - 1]?.month);
    const closest = closestMonth(points);
    const marketSummary = `How much the market looked like today by month, ${first} to ${lastShown}`
        + (closest ? `. Most like today: ${monthLabel(closest.month)}, ${Math.round(closest.score)} out of 100.` : '.');
    const deepestOf = (label, key) => {
        const low = points.reduce((worst, point) => (Number.isFinite(point[key]) && (!worst || point[key] < worst[key]) ? point : worst), null);
        return low ? [`${label} ${formatPercent(low[key], 1)} in ${monthLabel(low.month)}`] : [];
    };
    const deepest = rows.flatMap((row, at) => deepestOf(row.label, `drawdown${at}`));
    const actualDeepest = markets.flatMap((market, at) => (market.line ? deepestOf(market.label, `market${at}`) : []));
    const replaySummary = `Today’s holdings replayed through past factor returns by month, ${first} to ${lastShown}`
        + (deepest.length ? `. Deepest below the high in these years: ${deepest.join('; ')}.` : '.')
        + (actualDeepest.length ? ` For reference, the markets’ actual deepest drawdowns: ${actualDeepest.join('; ')}.` : '');
    const actual = replayed && markets.some((market) => market.line);
    const thin = rows.some((row, at) => row.line && points.some((point) => thinMonth(point, at)));
    // A span of at least a year, which presses the toggle that shows the same months.
    const pickYears = (_, [from, to], thumb) => {
        const width = MIN_SPAN_MONTHS - 1;
        let start = from;
        let end = to;
        if (end - start < width) {
            if (thumb === 0) start = Math.max(0, end - width);
            end = Math.min(last, start + width);
            start = Math.max(0, end - width);
        }
        setYears({ from: all[start].month, to: all[end].month });
    };
    const choosable = all.length > MIN_SPAN_MONTHS && (scored || replayed);
    // The closest past pattern is outlined where it meets the years shown; when
    // the history has more of it than they show, the shortest lookback that
    // holds it shows it all. A pattern from before the history's first month
    // is as shown as it can be once that month is.
    const pattern = runUp && scored && points.length ? runUp : null;
    const outlined = Boolean(pattern) && pattern.to >= points[0].month && pattern.from <= points[points.length - 1].month;
    const cut = Boolean(pattern) && ((lo > 0 && pattern.from < points[0].month) || (hi < last && pattern.to > points[points.length - 1].month));
    const showPattern = () => {
        const key = lookbacks.find((option) => all[startOf(option)].month <= pattern.from)?.key ?? ALL;
        setYears(key);
        // Its note and button go, so focus moves to the toggle now pressed, scrolled into view if need be.
        lookbackGroup.current?.querySelector(`button[value="${key}"]`)?.focus();
    };
    const shownBands = events.length > 0 && Boolean(eventBands(events, points.map((point) => point.month)).length);
    const caption = [
        replayed && 'Each replay line is how far a portfolio would sit below its highest point in the years shown, starting from zero, if today’s holdings had been held through them: a what-if, not a record.',
        actual && 'Lines marked “actual” are the markets’ real drawdowns below their highest point in the same years, from ETF prices with dividends reinvested: a record, not a replay. They compound, while a replay adds up each day’s result at today’s sizes.',
        shownBands && `Shaded bands are the events in the table: ${bandKinds(events)}. Click a band to open its event in the grid.`,
        outlined && `The outlined months are the run-up to ${pattern.name}, the closest past pattern to today’s readings.`,
        'Drag across either panel to make those months a new event.',
    ].filter(Boolean).join(' ');

    return (
        <Box sx={{ display: 'grid', gap: 0.75, minWidth: 0 }}>
            {choosable && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 1, rowGap: 0.5, pl: PLOT_INDENT }}>
                    <Typography variant="caption" sx={performanceCompactLabelSx}>Lookback:</Typography>
                    {/* Pressing the pressed toggle passes null; it keeps those months. */}
                    <ToggleButtonGroup ref={lookbackGroup} exclusive size="small" value={pressed} onChange={(_, key) => setYears(key ?? pressed)}
                        aria-label="Lookback" sx={{ height: 28, ...performanceToggleGroupSx, '& .MuiToggleButtonGroup-grouped': { border: 'none' } }}>
                        {[...lookbacks.map((option) => option.key), ALL].map((key) => (
                            <ToggleButton key={key} value={key} sx={{ px: 1.2, ...performanceToggleButtonSx }}>{key}</ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                    <Typography variant="caption" color="text.secondary">{first} – {lastShown}</Typography>
                </Box>
            )}
            <Box role="group" aria-label="Factor history timeline" onMouseLeave={leave}
                // A drag released over a title or the legend, between the charts, still ends;
                // a press released there is not a click on a band.
                onMouseUp={() => (drag.current?.moved ? pointer.current.release(null, null) : endDrag())}
                // The browser took the touch to scroll the page.
                onTouchCancel={leave}
                sx={{ position: 'relative', display: 'grid', gap: 0.75, minWidth: 0, cursor: 'crosshair', userSelect: 'none', touchAction: 'pan-y' }}>
                <PanelTitle title="How much the market looked like today" note={contextNote} />
                {scored ? (
                    <Box role="img" aria-label={marketSummary} sx={{ position: 'relative', height: MARKET_HEIGHT + (replayed ? 0 : X_AXIS_HEIGHT) }}>
                        {charts.market}
                        {overlays(MARKET_MARGIN.bottom + (replayed ? 0 : X_AXIS_HEIGHT), !replayed)}
                        {tooltip}
                    </Box>
                ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ pl: PLOT_INDENT }}>
                        The market line appears once today can be compared with the past.
                    </Typography>
                )}
                <PanelTitle title="Today’s holdings replayed through past factor returns, not their actual history"
                    note={actual ? 'with the markets’ actual drawdowns for reference' : null} />
                {replayed ? (
                    <>
                        <Box component="ul" aria-label="Portfolios replayed" sx={{
                            display: 'flex', flexWrap: 'wrap', columnGap: 1.5, rowGap: 0.25, m: 0, p: 0, pl: PLOT_INDENT,
                            listStyle: 'none', fontSize: 12, color: 'text.secondary',
                        }}>
                            {rows.map((row, at) => (
                                <Box component="li" key={row.key} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                                    {row.line && <LineKey color={colors[at]} width={row.baseline ? 3 : 2} />}
                                    <span>{row.label}{row.baseline ? ' (baseline)' : ''}{STATUS_NOTES[row.status] ? ` · ${STATUS_NOTES[row.status]}` : ''}</span>
                                </Box>
                            ))}
                            {thin && (
                                <Box component="li" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                                    <LineKey color={chrome.mutedText} dashed />
                                    <span>more than {Math.round(HIGH_UNCOVERED_SHARE * 100)}% of the exposure without factor history</span>
                                </Box>
                            )}
                        </Box>
                        {markets.length > 0 && (
                            <Box component="ul" aria-label="Markets, actual history" sx={{
                                display: 'flex', flexWrap: 'wrap', columnGap: 1.5, rowGap: 0.25, m: 0, p: 0, pl: PLOT_INDENT,
                                listStyle: 'none', fontSize: 12, color: 'text.secondary',
                            }}>
                                {markets.map((market) => (
                                    <Box component="li" key={market.key} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                                        {market.line && <LineKey color={chrome.secondaryText} width={1.5} dash={marketDash(market.market)} />}
                                        <span>{market.label} · actual{MARKET_STATUS_NOTES[market.status] ? ` · ${MARKET_STATUS_NOTES[market.status]}` : ''}</span>
                                    </Box>
                                ))}
                            </Box>
                        )}
                        <Box role="img" aria-label={replaySummary} sx={{ position: 'relative', height: REPLAY_HEIGHT }}>
                            {charts.replay}
                            {overlays(X_AXIS_HEIGHT, true)}
                            {!scored && tooltip}
                        </Box>
                    </>
                ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ pl: PLOT_INDENT }}>
                        {rows.some((row) => row.status === 'loading') ? 'The replays appear once the grid has them.' : 'None of these portfolios can be replayed.'}
                    </Typography>
                )}
            </Box>
            {choosable && (
                <Box sx={{ display: 'grid', pl: `${AXIS_WIDTH}px`, pr: `${MARGIN.right}px` }}>
                    {/* As wide as the plots, its track the whole history: the thumbs mark the years shown. */}
                    <Slider size="small" value={[lo, hi]} min={0} max={last} step={1} disableSwap onChange={pickYears}
                        getAriaLabel={(thumb) => (thumb === 0 ? 'Timeline starts' : 'Timeline ends')}
                        getAriaValueText={(value) => monthLabel(all[value]?.month)}
                        valueLabelDisplay="auto" valueLabelFormat={(value) => monthLabel(all[value]?.month)} />
                    {/* Below the plots and the slider, so a note that comes and goes mid-drag moves neither. */}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 1, minHeight: 30 }}>
                        <Typography id={noteId} variant="caption" color="text.secondary">
                            {cut ? `The closest past pattern, ${monthLabel(pattern.from)} – ${monthLabel(pattern.to)}, is ${outlined ? 'partly ' : ''}outside these years.`
                                : 'Drag the slider’s ends to show any other years.'}
                        </Typography>
                        {cut && <Button size="small" onClick={showPattern} aria-describedby={noteId} sx={{ minWidth: 0, py: 0 }}>Show it</Button>}
                    </Box>
                </Box>
            )}
            <Typography variant="caption" color="text.secondary">{caption}</Typography>
        </Box>
    );
}

HistoryStrip.propTypes = {
    months: PropTypes.arrayOf(PropTypes.shape({
        month: PropTypes.string.isRequired, score: PropTypes.number, excluded: PropTypes.bool,
    })).isRequired,
    rows: PropTypes.arrayOf(PropTypes.shape({
        key: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        baseline: PropTypes.bool,
        line: PropTypes.shape({
            months: PropTypes.array, close: PropTypes.array, high: PropTypes.array, low: PropTypes.array,
            within_month_drawdown: PropTypes.array, uncovered_exposure_share: PropTypes.array,
        }),
        status: PropTypes.oneOf(['loading', 'unavailable']),
    })),
    markets: PropTypes.arrayOf(PropTypes.shape({
        key: PropTypes.string.isRequired,
        market: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        line: PropTypes.shape({
            months: PropTypes.array, close: PropTypes.array, high: PropTypes.array, low: PropTypes.array, within_month_drawdown: PropTypes.array,
        }),
        status: PropTypes.oneOf(['loading', 'unavailable']),
    })),
    events: PropTypes.array,
    selectedEventId: PropTypes.string,
    runUp: PropTypes.shape({ from: PropTypes.string.isRequired, to: PropTypes.string.isRequired, name: PropTypes.string.isRequired }),
    contextNote: PropTypes.string.isRequired,
    baselineLabel: PropTypes.string,
    selection: PropTypes.shape({ from: PropTypes.string.isRequired, to: PropTypes.string.isRequired }),
    onSelect: PropTypes.func.isRequired,
    onSelectEvent: PropTypes.func.isRequired,
    anchorRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })]),
};
